import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, ChevronRight, ArrowLeft, FileText } from 'lucide-react';
import Header from '../components/Header';
import { modules, getModuleIcon } from '../data/modules';

function highlightText(text, query) {
  if (!query || query.length < 2) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.replace(regex, '<mark class="search-highlight">$1</mark>');
}

function getSnippet(content, query, maxLen = 200) {
  if (!query || query.length < 2) return content.substring(0, maxLen) + '...';
  const lower = content.toLowerCase();
  const idx = lower.indexOf(query.toLowerCase());
  if (idx === -1) return content.substring(0, maxLen) + '...';
  const start = Math.max(0, idx - 60);
  const end = Math.min(content.length, idx + maxLen);
  let snippet = content.substring(start, end);
  if (start > 0) snippet = '...' + snippet;
  if (end < content.length) snippet = snippet + '...';
  return snippet;
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQuery);

  const results = useMemo(() => {
    const q = (searchParams.get('q') || '').toLowerCase().trim();
    if (!q || q.length < 2) {
      // Show all modules when no query
      return modules.map(mod => ({
        type: 'module',
        module: mod,
        stepIndex: null,
        step: null,
        relevance: 1
      }));
    }

    const found = [];
    modules.forEach(mod => {
      const titleMatch = mod.title.toLowerCase().includes(q);
      const descMatch = mod.description.toLowerCase().includes(q);
      const subtitleMatch = mod.subtitle.toLowerCase().includes(q);

      if (titleMatch || descMatch || subtitleMatch) {
        found.push({
          type: 'module',
          module: mod,
          stepIndex: null,
          step: null,
          relevance: titleMatch ? 10 : 5
        });
      }

      mod.steps.forEach((step, idx) => {
        const stepTitleMatch = step.title.toLowerCase().includes(q);
        const contentMatch = step.content.toLowerCase().includes(q);

        if (stepTitleMatch || contentMatch) {
          found.push({
            type: 'step',
            module: mod,
            stepIndex: idx,
            step: step,
            relevance: stepTitleMatch ? 8 : 3
          });
        }
      });
    });

    return found.sort((a, b) => b.relevance - a.relevance);
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ q: query });
  };

  const q = searchParams.get('q') || '';

  return (
    <div className="min-h-screen bg-background" data-testid="search-page">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <Link to="/k4" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#2563EB] mb-6 transition-colors" data-testid="search-back-btn">
          <ArrowLeft className="w-3 h-3" /> Back to K4 Resource
        </Link>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              data-testid="search-page-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the K4 resource library..."
              className="w-full pl-12 pr-28 py-4 text-base bg-background border-2 border-border rounded-2xl focus:outline-none focus:border-[#2563EB] focus:ring-4 focus:ring-[#2563EB]/15 transition-all"
              autoFocus
            />
            <button
              type="submit"
              data-testid="search-page-btn"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
            >
              Search
            </button>
          </div>
        </form>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            {q ? (
              <>Found <strong className="text-foreground">{results.length}</strong> results for "<strong className="text-[#2563EB]">{q}</strong>"</>
            ) : (
              <>Showing all <strong className="text-foreground">{modules.length} modules</strong></>
            )}
          </p>
        </div>

        {/* Results */}
        <div className="space-y-3" data-testid="search-results">
          {results.map((result, i) => {
            const Icon = getModuleIcon(result.module.icon);
            return (
              <Link
                key={i}
                to={result.type === 'module'
                  ? `/module/${result.module.id}`
                  : `/module/${result.module.id}?step=${result.stepIndex}`
                }
                data-testid={`search-result-${i}`}
                className="block border border-border rounded-xl p-5 hover:border-[#2563EB]/30 hover:shadow-sm transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${result.module.color}12` }}
                  >
                    {result.type === 'module' ? (
                      <Icon className="w-5 h-5" style={{ color: result.module.color }} strokeWidth={1.5} />
                    ) : (
                      <FileText className="w-5 h-5" style={{ color: result.module.color }} strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {result.type === 'module' ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#2563EB]">Module</span>
                        </div>
                        <h3
                          className="font-['Outfit'] text-base font-semibold text-foreground group-hover:text-[#2563EB] mt-0.5"
                          dangerouslySetInnerHTML={{ __html: highlightText(result.module.title, q) }}
                        />
                        <p
                          className="text-sm text-muted-foreground mt-1 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: highlightText(result.module.description, q) }}
                        />
                        <span className="inline-block text-xs text-muted-foreground mt-2">{result.module.steps.length} steps</span>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#2563EB]">Step {result.stepIndex + 1}</span>
                          <span className="text-[10px] text-muted-foreground">{result.module.title}</span>
                        </div>
                        <h3
                          className="font-['Outfit'] text-base font-semibold text-foreground group-hover:text-[#2563EB] mt-0.5"
                          dangerouslySetInnerHTML={{ __html: highlightText(result.step.title, q) }}
                        />
                        <p
                          className="text-sm text-muted-foreground mt-1 leading-relaxed line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: highlightText(getSnippet(result.step.content, q), q) }}
                        />
                      </>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-pink-400 shrink-0 mt-1" />
                </div>
              </Link>
            );
          })}

          {results.length === 0 && (
            <div className="text-center py-16" data-testid="no-results">
              <Search className="w-12 h-12 text-slate-200 mx-auto mb-4" />
              <h3 className="font-['Outfit'] text-lg font-semibold text-foreground mb-1">No results found</h3>
              <p className="text-sm text-muted-foreground">Try different keywords or browse the modules from the home page.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
